import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, UntypedFormGroup } from '@angular/forms';
import { StateService } from '../../services/state/state.service';
import { StrategyName } from '../../services/assignment-strategy/strategy/strategy-name';
import { BookingService } from '../../services/booking/booking.service';
import { Strategy } from '../../services/assignment-strategy/strategy/strategy';
import { SessionStorageService } from '../../services/session-storage/session-storage.service';
import { StrategyFileNumber } from '../../services/assignment-strategy/strategy/strategy-file-number';
import { SelectOptionType } from './types';
import { FileNumberBuilderService } from '../../services/assignment-strategy/file-number-builder.service';
import { PersonalDataForm } from '../../api/models/personal-data-form';
import { DinSpec91379Service } from '../personal-data/dynamic-form/form-group-factory-service/form-field-validator-mapper-service/custom-validators/dinSpec91379/din-spec-91379.service';
import { StrategyList } from '../../services/assignment-strategy/strategy/strategy-list';
import { StrategyRandom } from '../../services/assignment-strategy/strategy/strategy-random';
import { AccessibilityService } from '../../services/accessibility.service';

@Component({
    selector: 'otb-assignment-strategy',
    templateUrl: './assignment-strategy.component.html',
    styleUrls: ['./assignment-strategy.component.scss'],
    standalone: false,
})

//todo: This Component should be split, https://publicplan.atlassian.net/browse/TEPG-3232
export class AssignmentStrategyComponent implements OnInit {
    @ViewChild('mainForm') mainForm!: ElementRef<HTMLFormElement>;
    strategy: Strategy;
    strategyForm: UntypedFormGroup;
    departmentNumbers: SelectOptionType[] = [];
    registrationMarks: SelectOptionType[] = [];
    years: SelectOptionType[] = [];

    namePlaceholder: string = 'ggf.: Di, Von, Zu, Al-, El-, Da';
    lastNameLabel: string = 'Nachname';
    personalDataForm: PersonalDataForm;

    listEntries: Array<string> = [];

    constructor(
        private stateService: StateService,
        private bookingService: BookingService,
        private sessStorageService: SessionStorageService,
        private fileNumberBuilderService: FileNumberBuilderService,
        private dinSpec91379Service: DinSpec91379Service,
        private accessibility: AccessibilityService,
    ) {}

    ngOnInit(): void {
        this.strategy = this.bookingService.booking.strategy;
        this.personalDataForm = this.bookingService.booking.personalDataForm;
        this._buildFormGroup();
    }

    _buildFormGroup(): void {
        this.strategyForm = this.strategy.buildForm(this.personalDataForm, this.dinSpec91379Service);
        if (this.strategy instanceof StrategyName) {
            this.lastNameLabel = this.resolveLastNameLabel(this.strategy);
        }
        if (this.strategy instanceof StrategyList) {
            this.listEntries = this.strategy.options;
        }
        if (this.strategy instanceof StrategyFileNumber) {
            this.departmentNumbers = this.toSelectOptions(this.strategy.departmentNumbers);
            this.registrationMarks = this.toSelectOptions(this.strategy.registrationMarks);
            this.years = this.toSelectOptions(this.strategy.years);
        }
    }

    _setStrategyData(): void {
        this.strategy.applyFormValues(this.strategyForm);
        this.bookingService.booking.strategy = this.strategy;
        this.writeToSessionStorage();
    }

    _markFormAsDirty(): void {
        this.strategy.markDirty(this.strategyForm);
    }

    private writeToSessionStorage(): void {
        const sessData = this.sessStorageService.getPersonalData();
        if (this.strategy instanceof StrategyRandom || sessData === null) {
            return;
        }

        if (this.strategy instanceof StrategyName) {
            if (sessData?.lastName) {
                sessData.lastName = this.strategy.fullName;
            }
        }

        if (this.strategy instanceof StrategyList) {
            sessData.choosenOption = this.strategy.chosenOption;
        }

        if (this.strategy instanceof StrategyFileNumber) {
            sessData[this.strategy.fileNumberFormField] = this.fileNumberBuilderService.buildFromStrategy(
                this.strategy,
            );
        }

        this.sessStorageService.persistPersonalData(JSON.stringify(sessData));
    }

    private resolveLastNameLabel(strategy: StrategyName): string {
        const matchingField = this.personalDataForm.form_fields.find(
            (field) => field.name === strategy.lastNameFormField,
        );
        return matchingField?.label ?? this.lastNameLabel;
    }

    private toSelectOptions(values: Array<string>): SelectOptionType[] {
        return values.map((value) => ({ value, label: value }));
    }

    onSubmit(): void {
        if (!this.strategyForm) {
            throw new Error('Strategy form is not defined');
        }

        if (this.strategyForm.valid) {
            this._setStrategyData();
            this.stateService.next();
        } else {
            this._markFormAsDirty();
            this.accessibility.focusFirstInvalidField(this.mainForm.nativeElement);
            return;
        }
    }

    get name(): AbstractControl {
        return this.strategyForm.get('name') as AbstractControl;
    }

    get strategyName(): string {
        return this.bookingService.booking.strategy.strategyName;
    }
}
